import { Link, NavLink } from 'react-router';
import { Plus } from 'lucide-react';
import type { Session, Space } from '@product-portal/shared';
import { navigationFor, spaceLinksFor } from '@/lib/navigation';
import { cn } from '@/lib/utils';
import { buttonVariants } from '@/components/ui/button';
import { UserMenu } from './user-menu';

const HOME: Record<Space, string> = {
  business: '/',
  product: '/produit/qualification',
  admin: '/admin/equipes',
};

const NAV_LABEL: Record<Space, string> = {
  business: 'Navigation principale',
  product: 'Navigation équipe produit',
  admin: 'Navigation administration',
};

function SpaceBadge({ space }: { space: Space }) {
  if (space === 'business') return null;
  return (
    <span
      className={cn(
        'rounded-tag border-[1.5px] border-header-badge px-2 py-[3px] text-[11px] font-bold uppercase tracking-[0.08em]',
        space === 'admin' ? 'bg-header-badge text-header-badge-foreground' : 'text-header-badge',
      )}
    >
      {space === 'admin' ? 'Admin' : 'Produit'}
    </span>
  );
}

/**
 * Header of a space: 4px brand gradient rule, logo (40px), space badge,
 * navigation filtered by the role read from the token. Entries a role cannot
 * use are not rendered — but the API is what actually refuses access.
 */
export function AppHeader({
  space,
  session,
  wide = false,
  badges = {},
}: {
  space: Space;
  session: Session;
  wide?: boolean;
  /** Counter shown next to an entry, by its path (« Qualification 7 » of the product header). */
  badges?: Record<string, number | undefined>;
}) {
  const items = navigationFor(space, session.role);
  const links = spaceLinksFor(space, session.role);
  return (
    <header className="border-b border-border bg-card">
      <div aria-hidden="true" className="h-1 bg-brand-gradient" />
      <div className={cn('mx-auto flex min-h-[68px] flex-wrap items-center gap-6 px-6', wide ? 'max-w-[1392px]' : 'max-w-[1200px]')}>
        <Link to={HOME[space]} className="flex items-center gap-3 no-underline">
          <img src="/product-portal-logo.png" alt="Product Portal — Europa Group" className="block h-10 w-auto" />
          <SpaceBadge space={space} />
        </Link>
        <nav aria-label={NAV_LABEL[space]} className="flex flex-wrap gap-1">
          {items.map((item) => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end ?? false}
              className={({ isActive }) =>
                cn(
                  'inline-flex items-center rounded-control px-3 py-2 text-sm no-underline',
                  isActive ? 'bg-accent font-bold text-accent-foreground hover:text-accent-foreground' : 'text-foreground-secondary hover:text-foreground',
                )
              }
            >
              {item.label}
              {badges[item.to] ? (
                <span className="ml-2 inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-primary px-1.5 text-[11px] font-bold text-primary-foreground">
                  {badges[item.to]}
                </span>
              ) : null}
            </NavLink>
          ))}
        </nav>
        <div className="ml-auto flex items-center gap-3.5">
          {links.map((link) => (
            <Link key={link.to} to={link.to} className="text-[13px] font-bold no-underline">
              {link.label}
            </Link>
          ))}
          {space === 'business' && (
            <Link to="/demandes/nouvelle" className={cn(buttonVariants(), 'no-underline')}>
              <Plus aria-hidden="true" className="size-4" strokeWidth={2.2} />
              Nouvelle demande
            </Link>
          )}
          <UserMenu session={session} />
        </div>
      </div>
    </header>
  );
}
